import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ForgotPasswordScreenImage } from "../../../assets";
import {
  Button,
  AuthSplitLayout,
  AuthCenteredLayout,
  BackToSignIn,
} from "../../../components";
import { forgotPasswordApi } from "../../../DAL";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState("forgot"); // "forgot" | "check-email"
  const [submittedEmail, setSubmittedEmail] = useState("");

  const schema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .email(t("validation.emailInvalid"))
          .required(t("validation.emailRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const mutation = useMutation({
    mutationFn: forgotPasswordApi,
    onSuccess: (_, variables) => {
      setSubmittedEmail(variables.email);
      setStep("check-email");
    },
  });

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  const imageOverlay = (
    <div className="absolute top-40 px-16">
      <p className="text-white text-[2.75rem] font-medium leading-snug drop-shadow-lg">
        {t("forgotPassword.imageQuote")}
      </p>
      <p className="text-white text-2xl font-light mt-4 drop-shadow-lg">
        {t("forgotPassword.imageAuthor")}
      </p>
    </div>
  );

  // ─── Check Your Email Screen (no image panel) ─────────────────────────────
  if (step === "check-email") {
    return (
      <AuthCenteredLayout maxWidthClass="max-w-md">
        <div className="flex flex-col items-center text-center">
          <div className="text-3xl sm:text-4xl font-medium text-black mb-3">
            {t("forgotPassword.checkEmailTitle")}
          </div>
          <div className="text-base sm:text-lg mb-8 font-semibold">
            {t("forgotPassword.checkEmailSubtitle")}{" "}
            <span className="text-primary-500">{submittedEmail}</span>
          </div>

          <p className="text-base sm:text-lg font-semibold mt-2">
            {t("forgotPassword.didntReceive")}{" "}
            <span
              className="text-primary-500 cursor-pointer font-medium"
              onClick={() => {
                mutation.reset();
                setStep("forgot");
              }}
            >
              {t("forgotPassword.clickResend")}
            </span>
          </p>

          <BackToSignIn className="mt-8" />
        </div>
      </AuthCenteredLayout>
    );
  }

  // ─── Forgot Password Screen (with image panel) ────────────────────────────
  return (
    <AuthSplitLayout
      imageSrc={ForgotPasswordScreenImage}
      imageAlt="forgot-password-screen-image"
      imageOverlay={imageOverlay}
      panelClassName="h-full"
      footer={<BackToSignIn />}
    >
      <div className="w-full max-w-sm lg:w-[50%]">
        <div className="text-3xl sm:text-4xl font-medium text-black mb-2">
          {t("forgotPassword.title")}
        </div>
        <div className="text-sm sm:text-base mb-8 text-gray-500">
          {t("forgotPassword.subtitle")}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6">
            <label className="font-medium text-sm">
              {t("forgotPassword.emailLabel")}
            </label>
            <input
              type="email"
              placeholder={t("forgotPassword.emailPlaceholder")}
              {...register("email")}
              className="w-full border rounded-lg p-3 mt-2"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            loading={mutation.isPending}
            fullWidth
            size="lg"
          >
            {mutation.isPending
              ? t("forgotPassword.btnLoading")
              : t("forgotPassword.btnSubmit")}
          </Button>
        </form>
      </div>
    </AuthSplitLayout>
  );
};

export default ForgotPassword;
